import dayjs from "dayjs"

export interface DurationParts {
  startTime?: string | null
  endTime?: string | null
  hours?: number | null
  minutes?: number | null
}

/** Resolve an entry's duration from explicit hours/minutes, or from start/end times. */
const getHoursAndMinutes = (e: DurationParts): { hours: number; minutes: number } => {
  if (e.hours != null || e.minutes != null) {
    return { hours: e.hours ?? 0, minutes: e.minutes ?? 0 }
  }
  if (e.startTime && e.endTime) {
    const diff = Math.max(0, dayjs(e.endTime).diff(dayjs(e.startTime), "minute"))
    return { hours: Math.floor(diff / 60), minutes: diff % 60 }
  }
  return { hours: 0, minutes: 0 }
}

/** Duration expressed as decimal hours (e.g. 1h 30m → 1.5), for money math. */
export const getDecimalHours = (e: DurationParts): number => {
  const { hours, minutes } = getHoursAndMinutes(e)
  return hours + minutes / 60
}

/**
 * Locale-neutral duration in H:MM form, e.g. "1:30", "0:45", "12:05".
 * Colon format matches QuickBooks/timesheet convention and avoids the
 * decimal-separator and h/m-abbreviation ambiguity across locales.
 */
export const getDuration = (e: DurationParts): string => {
  const { hours, minutes } = getHoursAndMinutes(e)
  return `${hours}:${String(minutes).padStart(2, "0")}`
}

/** Whole minutes in the same H:MM form as `getDuration`. */
export const formatMinutes = (minutes: number): string =>
  `${Math.floor(minutes / 60)}:${String(Math.round(minutes % 60)).padStart(2, "0")}`

/** Whole minutes as decimal hours, matching how the API rounds an invoice line. */
export const minutesToDecimalHours = (minutes: number): number =>
  Math.round((minutes / 60) * 100) / 100

/**
 * A typed duration as whole minutes, or null if it can't be read.
 *
 * Deliberately forgiving about *how* the duration is written, because the
 * habits differ: "1:30" is the timesheet convention, "1.5" is what people who
 * bill in decimal hours type, and "1h 30m" is what everyone else reaches for. A
 * bare number is hours — the same reading QuickBooks gives a Qty — so "2" is
 * two hours, and minutes need their unit ("45m").
 */
export const parseDuration = (input: string): number | null => {
  const text = input.trim().toLowerCase()
  if (!text) return null

  const colon = text.match(/^(\d+):([0-5]?\d)$/)
  if (colon) return Number(colon[1]) * 60 + Number(colon[2])

  const hoursAndMinutes = text.match(/^(?:(\d+(?:\.\d+)?)\s*h(?:rs?|ours?)?)?\s*(?:(\d+)\s*m(?:ins?|inutes?)?)?$/)
  if (hoursAndMinutes && (hoursAndMinutes[1] || hoursAndMinutes[2])) {
    return Math.round(Number(hoursAndMinutes[1] ?? 0) * 60) + Number(hoursAndMinutes[2] ?? 0)
  }

  if (/^\d+(?:\.\d+)?$/.test(text)) return Math.round(Number(text) * 60)

  return null
}
